import { ArrowClockwiseIcon, PlusIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react'
import { utilities } from '@pixee/design-system'
import { useCallback, useEffect, useRef, useState } from 'react'
import { match, P } from 'ts-pattern'
import { SUPPORT_EMAIL } from '../../constants.ts'
import { useToastService } from '../../services/toast-service.tsx'
import { useGetCodingGuidelines } from '../../utils/api-client/api-hooks.ts'
import { type CodingGuidelinesResponse } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './coding-guidelines-tab.css.ts'
import { PROCESSING_FAILED_MESSAGE, type UploadJob, type UploadJobStatus } from './coding-guidelines-types.ts'
import { DeleteConfirmModal } from './delete-confirm-modal.tsx'
import { DocumentCard } from './document-card.tsx'
import { EditDrawer } from './edit-drawer.tsx'
import { UploadActivityCard } from './upload-activity-card.tsx'
import { UploadDrawer } from './upload-drawer.tsx'
import {
  CODING_GUIDELINES_DOCUMENT_FAILED_PHASES,
  useCodingGuidelinesDocumentEvents,
  type CodingGuidelinesDocumentEvent,
} from './use-coding-guidelines-document-events.ts'

const DOC_CAP = 25
const AT_CAP_TOOLTIP_COPY = `You've reached the limit of ${DOC_CAP} coding guideline documents. Delete one before uploading another.`

type CodingGuidelinesTabProps = {
  organizationId?: string
  initialUploadJobs?: UploadJob[]
}

export const CodingGuidelinesTab: React.FC<CodingGuidelinesTabProps> = ({ organizationId, initialUploadJobs }) => {
  const { data: documents, isLoading, isError, refetch } = useGetCodingGuidelines({ organizationId })
  const { addToast } = useToastService()

  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [retryPrefillName, setRetryPrefillName] = useState<string | undefined>()
  const [editingDocument, setEditingDocument] = useState<CodingGuidelinesResponse | undefined>()
  const [deletingDocument, setDeletingDocument] = useState<CodingGuidelinesResponse | undefined>()
  const [uploadJobs, setUploadJobs] = useState<UploadJob[]>(initialUploadJobs ?? [])
  const uploadingTempIdRef = useRef<string | undefined>(undefined)

  const allDocuments = documents ?? []
  const isAtCap = allDocuments.length >= DOC_CAP
  const visibleJobs = uploadJobs.filter(j => !j.isDismissed)
  // Only reading/indexing jobs carry a real backend documentId; uploading jobs still hold the
  // client temp UUID (no document to stream yet) and ready/failed are terminal.
  const streamingJobs = uploadJobs.filter(
    j => !j.isDismissed && (j.status.phase === 'reading' || j.status.phase === 'indexing')
  )

  const handleDismissJob = (jobId: string) => {
    setUploadJobs(curr => curr.map(j => (j.id === jobId ? { ...j, isDismissed: true } : j)))
  }

  const handleDismissAll = () => {
    setUploadJobs(curr => curr.map(j => ({ ...j, isDismissed: true })))
  }

  const handleRetryJob = (jobId: string) => {
    const job = uploadJobs.find(j => j.id === jobId)
    setUploadJobs(curr => curr.filter(j => j.id !== jobId))
    setRetryPrefillName(job?.displayName)
    setIsUploadOpen(true)
  }

  const handleCloseUpload = () => {
    setIsUploadOpen(false)
    setRetryPrefillName(undefined)
  }

  const handleUploadStart = (displayName: string, file: File) => {
    const tempId = crypto.randomUUID()
    uploadingTempIdRef.current = tempId
    setUploadJobs(curr => [
      ...curr,
      {
        id: tempId,
        documentId: tempId,
        displayName: displayName || file.name,
        filename: file.name,
        fileSizeBytes: file.size,
        status: { phase: 'uploading', progressPercent: 0 },
        isDismissed: false,
      },
    ])
  }

  const handleUploadProgress = useCallback((progressPercent: number) => {
    const tempId = uploadingTempIdRef.current
    if (!tempId) {
      return
    }
    setUploadJobs(curr =>
      curr.map(j => (j.id === tempId ? { ...j, status: { phase: 'uploading', progressPercent } } : j))
    )
  }, [])

  const handleUploadSuccess = (document: CodingGuidelinesResponse, file: File) => {
    const tempId = uploadingTempIdRef.current
    uploadingTempIdRef.current = undefined
    setIsUploadOpen(false)
    setRetryPrefillName(undefined)

    const documentId = document.id
    if (documentId === undefined) {
      // Upload succeeded but the response carried no id, so we can't stream phases for it.
      // Drop the temp activity row and refetch; the document will appear in the list.
      if (tempId) {
        setUploadJobs(curr => curr.filter(j => j.id !== tempId))
      }
      void refetch()
      return
    }

    if (tempId) {
      setUploadJobs(curr =>
        curr.map(j =>
          j.id === tempId
            ? {
                ...j,
                id: documentId,
                documentId,
                displayName: document.display_name ?? '',
                status: { phase: 'reading' },
              }
            : j
        )
      )
    } else {
      setUploadJobs(curr => [
        ...curr,
        {
          id: documentId,
          documentId,
          displayName: document.display_name ?? '',
          filename: file.name,
          fileSizeBytes: file.size,
          status: { phase: 'reading' },
          isDismissed: false,
        },
      ])
    }
  }

  // An initial-upload failure isn't actionable from the activity row, so just drop the in-flight
  // row and let the drawer's error toast surface the failure with the drawer still open for retry
  // (per the upload-journey spec). Card rows with Retry are reserved for documents that fail
  // *after* upload, during processing. Filtering on an unset tempId is a harmless no-op.
  const handleUploadError = useCallback(() => {
    const tempId = uploadingTempIdRef.current
    uploadingTempIdRef.current = undefined
    setUploadJobs(curr => curr.filter(j => j.id !== tempId))
  }, [])

  const handleJobPhaseChange = useCallback((jobId: string, status: UploadJobStatus) => {
    setUploadJobs(curr => curr.map(j => (j.id === jobId ? { ...j, status } : j)))
  }, [])

  const handleJobReady = useCallback(
    (_jobId: string, displayName: string) => {
      void refetch()
      addToast({ message: <>&ldquo;{displayName}&rdquo; was successfully added</>, variant: 'success' })
    },
    [refetch, addToast]
  )

  const handleEditSuccess = () => {
    setEditingDocument(undefined)
  }

  const handleDeleteSuccess = () => {
    setDeletingDocument(undefined)
    setEditingDocument(undefined)
  }

  return (
    <div className={styles.container}>
      {/* Each streaming job mounts its own SSE listener; unmounts (and disconnects) when it reaches a terminal phase. */}
      {streamingJobs.map(job => (
        <CodingGuidelinesDocumentEventListener
          key={job.id}
          jobId={job.id}
          documentId={job.documentId}
          displayName={job.displayName}
          organizationId={organizationId}
          onPhaseChange={handleJobPhaseChange}
          onReady={handleJobReady}
        />
      ))}

      <div className={styles.toolbar}>
        <p className={styles.toolbarSubtitle}>
          Upload your organization's security policies and coding standards. Pixee uses them as additional context when
          analyzing findings.
        </p>
        {isAtCap ? (
          <>
            <TooltipWithHover
              trigger={
                <span className={styles.docCounterAtCap} tabIndex={0}>
                  <WarningIcon size={14} weight="fill" />
                  {isLoading ? '— / —' : `${allDocuments.length} / ${DOC_CAP} documents`}
                </span>
              }
            >
              {AT_CAP_TOOLTIP_COPY}
            </TooltipWithHover>
            <span id="at-cap-tooltip" role="tooltip" className={utilities.visuallyHidden}>
              {AT_CAP_TOOLTIP_COPY}
            </span>
          </>
        ) : (
          <span className={styles.docCounter}>
            {isLoading ? '— / —' : `${allDocuments.length} / ${DOC_CAP} documents`}
          </span>
        )}
        <DefaultButton
          buttonType="secondary"
          size="small"
          state={isAtCap ? 'disabled' : 'default'}
          aria-describedby={isAtCap ? 'at-cap-tooltip' : undefined}
          onClick={() => {
            if (!isAtCap) {
              setIsUploadOpen(true)
            }
          }}
        >
          <PlusIcon size={14} weight="bold" />
          Add document
        </DefaultButton>
      </div>

      <div className={styles.listArea}>
        {visibleJobs.length > 0 ? (
          <UploadActivityCard
            jobs={visibleJobs}
            onDismiss={handleDismissJob}
            onDismissAll={handleDismissAll}
            onRetry={handleRetryJob}
          />
        ) : null}

        {isLoading ? <LoadingSkeleton /> : null}

        {!isLoading && isError ? (
          <div className={styles.errorStateCard}>
            <p className={styles.errorStateTitle}>
              <WarningCircleIcon size={20} weight="fill" className={styles.errorStateIcon} />
              Couldn't load documents
            </p>
            <p className={styles.errorStateSubtitle}>
              We weren't able to retrieve your uploaded documents. Check your connection and try again.
              <br />
              If this keeps happening, please{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.errorStateSupportLink}>
                contact Support
              </a>
              .
            </p>
            <GhostButton
              size="small"
              icon={<ArrowClockwiseIcon size={14} weight="light" />}
              onClick={() => void refetch()}
            >
              Retry
            </GhostButton>
          </div>
        ) : null}

        {!isLoading && !isError && allDocuments.length === 0 ? (
          <div className={styles.emptyStateCard}>
            <p className={styles.emptyStateTitle}>No documents yet</p>
            <p className={styles.emptyStateSubtitle}>Upload a file to get started.</p>
          </div>
        ) : null}

        {!isLoading && !isError && allDocuments.length > 0 ? (
          <DocumentList documents={allDocuments} onEdit={doc => setEditingDocument(doc)} />
        ) : null}
      </div>

      {isUploadOpen ? (
        <UploadDrawer
          organizationId={organizationId}
          onSuccess={handleUploadSuccess}
          onCancel={handleCloseUpload}
          onUploadStart={handleUploadStart}
          onUploadProgress={handleUploadProgress}
          onUploadError={handleUploadError}
          prefillDisplayName={retryPrefillName}
        />
      ) : null}

      {editingDocument !== undefined ? (
        <EditDrawer
          organizationId={organizationId}
          document={editingDocument}
          onSuccess={handleEditSuccess}
          onCancel={() => setEditingDocument(undefined)}
          onDelete={() => setDeletingDocument(editingDocument)}
        />
      ) : null}

      {deletingDocument !== undefined ? (
        <DeleteConfirmModal
          organizationId={organizationId}
          document={deletingDocument}
          onCancel={() => setDeletingDocument(undefined)}
          onSuccess={handleDeleteSuccess}
        />
      ) : null}
    </div>
  )
}

// Maps backend SSE phases (CodingGuidelinesDocumentEventSchema) to UI UploadJobStatus.
// Backend phases: received → extracting → indexing → ready (or a failed_* state)
// UI phases: reading → indexing → ready (or failed)
const ssePhaseToJobStatus = (event: CodingGuidelinesDocumentEvent): UploadJobStatus =>
  match(event)
    .with({ phase: 'ready' }, () => ({ phase: 'ready' }) as const)
    .with({ phase: 'indexing' }, () => ({ phase: 'indexing' }) as const)
    .with({ phase: P.union('received', 'extracting') }, () => ({ phase: 'reading' }) as const)
    .with(
      { phase: P.union(...CODING_GUIDELINES_DOCUMENT_FAILED_PHASES) },
      failure => ({ phase: 'failed', errorMessage: failure.detail }) as const
    )
    .exhaustive()

type CodingGuidelinesDocumentEventListenerProps = {
  jobId: string
  documentId: string
  displayName: string
  organizationId?: string
  onPhaseChange: (jobId: string, status: UploadJobStatus) => unknown
  onReady: (jobId: string, displayName: string) => unknown
}

// One SSE subscription per active upload job. This is a component — not a
// useCodingGuidelinesDocumentEvents call inside CodingGuidelinesTab — because activeJobs is
// dynamic, and calling a hook inside a .map() over a variable-length list would violate the
// Rules of Hooks. Rendering one instance per job gives each its own subscription, connected
// and torn down by React on mount/unmount. Renders nothing.
const CodingGuidelinesDocumentEventListener: React.FC<CodingGuidelinesDocumentEventListenerProps> = ({
  jobId,
  documentId,
  displayName,
  organizationId,
  onPhaseChange,
  onReady,
}) => {
  const handleEvent = useCallback(
    (event: CodingGuidelinesDocumentEvent) => {
      const status = ssePhaseToJobStatus(event)
      onPhaseChange(jobId, status)
      if (event.phase === 'ready') {
        onReady(jobId, displayName)
      }
    },
    [jobId, displayName, onPhaseChange, onReady]
  )

  const { connectionStatus } = useCodingGuidelinesDocumentEvents({ organizationId, documentId, onEvent: handleEvent })

  // A terminal stream error (5xx, or reconnection give-up) never arrives as a
  // failure event, so map it onto the job ourselves — otherwise the row would
  // stay stuck mid-processing and never reflect the failure on the card.
  useEffect(() => {
    if (connectionStatus === 'error') {
      onPhaseChange(jobId, { phase: 'failed', errorMessage: PROCESSING_FAILED_MESSAGE })
    }
  }, [connectionStatus, jobId, onPhaseChange])

  return null
}

const LoadingSkeleton: React.FC = () => (
  <div aria-busy aria-label="Loading documents">
    {[1, 2, 3].map(i => (
      <div key={i} className={styles.skeletonRow} />
    ))}
  </div>
)

type DocumentListProps = {
  documents: CodingGuidelinesResponse[]
  onEdit: (document: CodingGuidelinesResponse) => unknown
}

const DocumentList: React.FC<DocumentListProps> = ({ documents, onEdit }) => (
  <div className={styles.list} role="list">
    {documents.map(doc => (
      <DocumentCard key={doc.id} document={doc} onEdit={onEdit} />
    ))}
  </div>
)
